import { gql, useQuery } from '@apollo/client';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import Button from '../components/Button';
import SVGIcon from '../components/SVGIcon';
import type {
  AllTopicsWithTemplatesQuery,
  AllTopicsWithTemplatesQueryVariables,
  TemplatesModalPersonalTemplatesQuery,
  TemplatesModalPersonalTemplatesQueryVariables,
  TemplatesModalTemplatePostContentQuery,
  TemplatesModalTemplatePostContentQueryVariables,
  TemplatesModalTopicFragment,
} from '../graphql/generated/graphqlTypes';
import closeIcon from '../icons/clear.svg';
import useDeletePost from '../post/useDeletePost';
import type { TreeListTopic } from '../topic/toTopicTreeList';
import toTopicTreeList, {
  flattenTopicTreeList,
} from '../topic/toTopicTreeList';
import useMembershipTopics from '../topic/useMembershipTopics';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import EmptyTemplate from './EmptyTemplate';
import TemplateContent from './TemplateContent';
import TemplateMobileHeader from './TemplateMobileHeader';
import styles from './TemplatesModal.module.scss';
import TemplatesSidebar from './TemplatesSidebar';

const TEMPLATES_MODAL_TEMPLATE_POST_FRAGMENT = gql`
  fragment TemplatesModalTemplatePost on Post {
    id
    ...TemplatesSidebar_templatePosts
  }
  ${TemplatesSidebar.fragments.templatePosts}
`;

const TEMPLATES_TOPIC_FRAGMENT = gql`
  fragment TemplatesModalTopic on Topic {
    id
    name
    allPosts(orderBy: UPDATED_AT, type: TEMPLATE) {
      edges {
        node {
          id
          ...TemplatesModalTemplatePost
        }
      }
    }
    inheritParent
    parent {
      id
      name
    }
    ancestors {
      id
      name
    }
    ...toTopicTreeList_topic
    ...TemplatesSidebar_topic
  }

  ${TEMPLATES_MODAL_TEMPLATE_POST_FRAGMENT}
  ${toTopicTreeList.fragments.topic}
  ${TemplatesSidebar.fragments.topic}
`;

const ALL_TOPICS_WITH_TEMPLATES_QUERY = gql`
  query allTopicsWithTemplates {
    session: currentSession {
      organization {
        id
        topics {
          id
          ...TemplatesModalTopic
        }
      }
    }
  }

  ${TEMPLATES_TOPIC_FRAGMENT}
`;

const filterTreeTopicsWithoutTemplatePosts = (
  topics?: TreeListTopic<TemplatesModalTopicFragment>[],
): TreeListTopic<TemplatesModalTopicFragment>[] => {
  if (!topics || topics.length === 0) {
    return [];
  }
  return topics
    .map((topic) => ({
      ...topic,
      childTopics: filterTreeTopicsWithoutTemplatePosts(topic.childTopics),
    }))
    .filter(
      (topic) =>
        topic.childTopics.length > 0 ||
        (topic.allPosts?.edges?.length ?? 0) > 0,
    );
};

const TEMPLATE_POST_CONTENT_QUERY = gql`
  query TemplatesModalTemplatePostContent($id: ID!) {
    post(id: $id) {
      id
      title
      content
      version
      topics {
        id
        ...TemplateContent_topic
      }
      owner {
        id
        ...TemplateContent_owner
      }
    }
  }
  ${TemplateContent.fragments.topic}
  ${TemplateContent.fragments.owner}
`;

const TEMPLATE_PERSONAL_TEMPLATES_QUERY = gql`
  query TemplatesModalPersonalTemplates($afterCursor: String) {
    session: currentSession {
      user {
        id
        posts(first: 100, filter: TEMPLATE, after: $afterCursor) {
          edges {
            node {
              id
              ...TemplatesSidebar_templatePosts
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        membershipTopics(includeGroups: true) {
          id
          ...useMembershipTopics_topic
        }
      }
    }
  }
  ${TemplatesSidebar.fragments.templatePosts}
  ${useMembershipTopics.fragments.topic}
`;

interface TemplatesPostModalProps {
  onCreatePost?: () => void;
  templateId?: string | null;
  close: () => void;
}

const TemplatesModal = ({
  onCreatePost,
  templateId = null,
  close,
}: TemplatesPostModalProps) => {
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(
    templateId,
  );
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(
    !activeTemplateId,
  );

  const { data, loading } = useQuery<
    TemplatesModalTemplatePostContentQuery,
    TemplatesModalTemplatePostContentQueryVariables
  >(TEMPLATE_POST_CONTENT_QUERY, {
    skip: !activeTemplateId,
    variables: { id: activeTemplateId ?? '' },
  });

  const {
    data: allTopicsWithTemplatePosts,
    refetch: refetchAllTopicsWithTemplatePosts,
  } = useQuery<
    AllTopicsWithTemplatesQuery,
    AllTopicsWithTemplatesQueryVariables
  >(ALL_TOPICS_WITH_TEMPLATES_QUERY);

  const personalTemplatesResult = useQuery<
    TemplatesModalPersonalTemplatesQuery,
    TemplatesModalPersonalTemplatesQueryVariables
  >(TEMPLATE_PERSONAL_TEMPLATES_QUERY);

  const fetchMore = ConnectionHelper.generateFetchMore(
    TEMPLATE_PERSONAL_TEMPLATES_QUERY,
    personalTemplatesResult,
    'session.user.posts',
  );

  const refetchPersonalTemplates = personalTemplatesResult.refetch;

  const templatePost = data?.post ?? null;
  const userTemplates = _.compact(
    personalTemplatesResult.data?.session.user.posts?.edges?.map(
      (e) => e?.node,
    ),
  );

  const allTopics = useMemo(() => {
    const list = toTopicTreeList<TemplatesModalTopicFragment>(
      allTopicsWithTemplatePosts?.session?.organization?.topics ?? [],
    );
    return flattenTopicTreeList(list);
  }, [allTopicsWithTemplatePosts]);

  const membershipTopics = useMembershipTopics(
    allTopics,
    personalTemplatesResult.data?.session?.user?.membershipTopics,
  );

  const topicsTree = useMemo(
    () => filterTreeTopicsWithoutTemplatePosts(membershipTopics ?? []),
    [membershipTopics],
  );

  const deletePost = useDeletePost();

  const createPost = () => {
    onCreatePost?.();
    close();
  };

  return (
    <div className={styles.container}>
      <div className={styles.modal}>
        <ClickOutsideHandler
          className={styles.sidebarContainer}
          onClickOutside={() => setIsMobileSidebarOpen(false)}
        >
          <TemplatesSidebar
            topicsTree={topicsTree}
            userTemplates={userTemplates}
            activeTemplateId={activeTemplateId}
            mobileOpen={isMobileSidebarOpen}
            loading={personalTemplatesResult.loading}
            onClickTemplate={setActiveTemplateId}
            fetchMore={fetchMore}
          />
        </ClickOutsideHandler>
        <div
          className={classNames(styles.contentContainer, {
            [styles.empty]: !activeTemplateId,
          })}
        >
          <TemplateMobileHeader
            openSidebar={() => setIsMobileSidebarOpen(true)}
            onClose={close}
          />
          {activeTemplateId ? (
            <TemplateContent
              template={templatePost}
              loading={loading}
              owner={templatePost?.owner ?? null}
              topics={templatePost?.topics ?? []}
              onCreatePost={createPost}
              deleteTemplate={async () => {
                if (!templatePost) return;
                await deletePost(templatePost.id);
                refetchAllTopicsWithTemplatePosts();
                refetchPersonalTemplates();

                setActiveTemplateId(null);
              }}
              onClose={close}
            />
          ) : (
            <EmptyTemplate
              hasTemplates={userTemplates.length > 0 || topicsTree.length > 0}
            />
          )}
        </div>
      </div>
      <Button className={styles.desktopClose} onClick={close}>
        <SVGIcon size="small">{closeIcon}</SVGIcon>
      </Button>
    </div>
  );
};

export default TemplatesModal;
